import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute component - Guards routes based on authentication and role
 * 
 * Usage:
 * <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><Component /></ProtectedRoute>} />
 */
export function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87B6A] mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check if user's role is allowed (if roles are specified)
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // User is authenticated but doesn't have permission
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500 mb-2">
              Your role: <span className="font-semibold text-gray-700 capitalize">{user.role}</span>
            </p>
            <p className="text-sm text-gray-500">
              Required: <span className="font-semibold text-gray-700 capitalize">{allowedRoles.join(' or ')}</span>
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/admin/dashboard'}
            className="w-full bg-[#E87B6A] text-white px-6 py-3 rounded-lg hover:bg-[#d66a59] transition-colors font-medium"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has correct role (or no role check specified)
  return children;
}
